import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import contractorActions from "../../../_actions/contractorActions";
import { toTitleCase } from "../../../_utils/generalUtils";
import AlertBox from "../../_common/AlertBox";

export default function EditContractorModalV2(props) {
  const {
    showModal,
    toggleModal,
    projectId,
    data,
    listRefreshCallback,
    submitCallback_new, // Callback after adding new
    submitCallback_existing, // Callback after editing existing
    asSupplierOnly,
  } = props;

  const dispatch = useDispatch();

  const fetching = useSelector((state) => state.contractors.fetching);
  const contractorData = !data.isNew
    ? useSelector(
        (state) => state.contractors.openContractorDetails[data.contractorId] || null
      )
    : null;

  const [name, setName] = useState(
    data.isNew && "searchString" in data
      ? toTitleCase(data.searchString || "")
      : contractorData
      ? contractorData.name
      : ""
  );
  const [abbreviation, setAbbreviation] = useState(
    data.isNew ? "" : contractorData ? contractorData.abbreviation : ""
  );
  const [isJointVenture, setIsJointVenture] = useState(
    data.isNew ? false : contractorData ? contractorData.isJointVenture : false
  );
  const [isDBE, setIsDBE] = useState(
    data.isNew ? false : contractorData ? contractorData.isDBE : false
  );
  const [isDVBE, setIsDVBE] = useState(
    data.isNew ? false : contractorData ? contractorData.isDVBE : false
  );
  const [isSupplier, setIsSupplier] = useState(
    asSupplierOnly
      ? true
      : data.isNew
      ? false
      : contractorData
      ? contractorData.isSupplier
      : false
  );

  const [formErrors, setFormErrors] = useState({
    name: null,
    abbreviation: null,
  });

  const validateForm = () => {
    const standardError = "Required";
    const _formErrors = {
      ...formErrors,
      name: !name ? standardError : null,
      abbreviation: !abbreviation ? standardError : null,
    };
    setFormErrors(_formErrors);
  };

  useEffect(() => validateForm(), []);
  useEffect(() => validateForm(), [name, abbreviation]);

  const formIsIncomplete = () =>
    !name || !abbreviation || formErrors.name || formErrors.abbreviation;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formIsIncomplete()) {
      const ctrData = {
        contractorId: !data.isNew ? data.contractorId : 0,
        projectId,
        name,
        abbreviation,
        isJointVenture,
        isDBE,
        isDVBE,
        isSupplier: asSupplierOnly ? true : isSupplier,
      };

      if (data.isNew) {
        const newContractorId = await dispatch(contractorActions.add(ctrData));
        listRefreshCallback && listRefreshCallback(data.searchString);
        submitCallback_new && submitCallback_new(newContractorId);
      } else {
        await dispatch(contractorActions.update(ctrData));
        listRefreshCallback && listRefreshCallback(data.searchString);
        submitCallback_existing && submitCallback_existing();
      }
      toggleModal();
    }
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );
  return (
    <React.Fragment>
      <Modal isOpen={showModal} size={"lg"} fade={false}>
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon icon="edit" className="fa-fw mr-2" />
          {data.isNew ? "Add Contractor" : "Edit Contractor"}
        </ModalHeader>
        <ModalBody>
          <div className="card m-2">
            <div className="card-body">
              {asSupplierOnly && (
                <div className="form-row mb-2">
                  <AlertBox color="info" icon="info-circle">
                    Only suppliers can be added during daily report input. Suppliers are
                    typically not listed as subcontractors in the Subcontracting List.
                    Examples are crane companies, owner-operators, concrete mixers, etc.
                  </AlertBox>
                </div>
              )}
              <div className="form-row mb-2">
                <div className="form-group col-md-9">
                  <label htmlFor="name" className="mb-0">
                    <b>Name:</b>*
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    className="form-control"
                    maxLength="50"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => validateForm()}
                  />
                  {formErrors.name && (
                    <span className="error-small">
                      <small>{formErrors.name}</small>
                    </span>
                  )}
                </div>
                <div className="form-group col-md-3">
                  <label htmlFor="abbreviation" className="mb-0">
                    <b>Abbrev.:</b>*
                  </label>
                  <input
                    type="text"
                    name="abbreviation"
                    id="abbreviation"
                    className="form-control"
                    maxLength="5"
                    value={abbreviation}
                    onChange={(e) => setAbbreviation(e.target.value)}
                    onBlur={() => validateForm()}
                  />
                  {formErrors.abbreviation && (
                    <span className="error-small">
                      <small>{formErrors.abbreviation}</small>
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row form-group mb-2">
                <div className="custom-control-inline custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="isJointVenture"
                    id="isJointVenture"
                    className="custom-control-input my-auto"
                    checked={isJointVenture}
                    onChange={(e) => {
                      setIsJointVenture(e.target.checked);
                      validateForm();
                    }}
                  />
                  <label
                    htmlFor="isJointVenture"
                    className="custom-control-label my-auto"
                  >
                    Joint venture
                  </label>
                </div>
              </div>
              <div className="form-row form-group mb-2">
                <div className="custom-control-inline custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="isDBE"
                    id="isDBE"
                    className="custom-control-input my-auto"
                    checked={isDBE}
                    onChange={(e) => {
                      setIsDBE(e.target.checked);
                      validateForm();
                    }}
                  />
                  <label htmlFor="isDBE" className="custom-control-label my-auto">
                    DBE
                  </label>
                </div>
              </div>
              <div className="form-row form-group mb-2">
                <div className="custom-control-inline custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="isDVBE"
                    id="isDVBE"
                    className="custom-control-input my-auto"
                    checked={isDVBE}
                    onChange={(e) => {
                      setIsDVBE(e.target.checked);
                      validateForm();
                    }}
                  />
                  <label htmlFor="isDVBE" className="custom-control-label my-auto">
                    DVBE
                  </label>
                </div>
              </div>
              <div className="form-row form-group mb-0">
                <div className="custom-control-inline custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="isSupplier"
                    id="isSupplier"
                    className="custom-control-input my-auto"
                    checked={isSupplier}
                    onChange={(e) => {
                      setIsSupplier(e.target.checked);
                      validateForm();
                    }}
                    disabled={asSupplierOnly}
                  />
                  <label htmlFor="isSupplier" className="custom-control-label my-auto">
                    Supplier (owner-operators, cranes, etc.)
                  </label>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <button
              type="button"
              className="btn btn-primary mr-2"
              name="editPcDetailsSubmit"
              onClick={handleSubmit}
              disabled={formIsIncomplete() || fetching}
            >
              {fetching && (
                <span className="mr-2 mt-1">
                  <FontAwesomeIcon icon="spinner" style={{ color: "white" }} spin />
                </span>
              )}
              {`${
                data.isNew ? (fetching ? "Adding" : "Add") : fetching ? "Saving" : "Save"
              }`}
            </button>
            <button className="btn btn-secondary" color="secondary" onClick={toggleModal}>
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}
